import { BaseModel } from '@cotera/client/v0/actions/framework/model.base';
import { useStatefullyAwareInScopeModel } from '@cotera/client/v0/actions/framework/use-in-scope-model';
import { Watchable } from '@cotera/client/v0/actions/framework/watchable';
import type { ArtifactDescriptor } from './artifacts';

/**
 * Model for programmatic control of the artifact panel (e.g. from command palette actions).
 * Access via getInScopeModelOfType(ChatArtifactModel) from ExecuteContext.
 */
export class ChatArtifactModel extends BaseModel {
  readonly pendingFocusArtifactIdWatchable: Watchable<string | null> =
    Watchable.fromValue<string | null>(null);
  readonly artifactsWatchable: Watchable<ArtifactDescriptor[]> =
    Watchable.fromValue<ArtifactDescriptor[]>([]);
  /** The artifact the panel is showing, so Share can mean "this one". */
  readonly activeArtifactIdWatchable: Watchable<string | null> =
    Watchable.fromValue<string | null>(null);

  constructor() {
    super('chat-artifact');
  }

  /** Request that the artifact panel focus/open the given artifact. */
  requestFocus(artifactId: string): void {
    this.pendingFocusArtifactIdWatchable.set(artifactId);
  }

  /** Clear any pending focus request. */
  clearPendingFocus(): void {
    this.pendingFocusArtifactIdWatchable.set(null);
  }

  /** Sync visible artifact panel entries (used by share and other chat-scoped actions). */
  setArtifacts(artifacts: ArtifactDescriptor[]): void {
    this.artifactsWatchable.set(artifacts);
  }

  setActiveArtifactId(artifactId: string | null): void {
    this.activeArtifactIdWatchable.set(artifactId);
  }
}

export const useChatArtifactModel = (): ChatArtifactModel | null =>
  useStatefullyAwareInScopeModel(ChatArtifactModel);
